/**
 * Bounded polling — every poll loop in the app should have a floor, a backoff,
 * a cap, and a hidden-tab pause. This replaces hand-rolled `setInterval` loops
 * that can run forever, keep hammering in background tabs, or outlive the page.
 */

export type TBoundedPollerOptions = {
  /** Called each tick; return `false` (or a promise of it) to stop polling. */
  tick: () => void | boolean | Promise<void | boolean>;
  /** Interval while the operation is fresh. Default 3s. */
  intervalMs?: number;
  /** After this much elapsed, slow to `slowIntervalMs`. Default 60s. */
  slowAfterMs?: number;
  /** Interval once `slowAfterMs` has passed. Default 10s. */
  slowIntervalMs?: number;
  /** Hard stop after this much elapsed regardless of ticks. Default 12 min. */
  maxDurationMs?: number;
  /** Skip ticks while the tab is hidden (the loop stays armed). Default true. */
  pauseWhenHidden?: boolean;
};

export type TBoundedPoller = {
  start: () => void;
  stop: () => void;
  isRunning: () => boolean;
};

export const createBoundedPoller = ({
  tick,
  intervalMs = 3_000,
  slowAfterMs = 60_000,
  slowIntervalMs = 10_000,
  maxDurationMs = 12 * 60_000,
  pauseWhenHidden = true,
}: TBoundedPollerOptions): TBoundedPoller => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = true;
  let startedAt = 0;

  const visible = () => !pauseWhenHidden || typeof document === "undefined" || document.visibilityState !== "hidden";

  const run = async () => {
    if (stopped) return;
    if (!visible()) {
      schedule();
      return;
    }
    const shouldContinue = await tick();
    if (shouldContinue === false) {
      stopped = true;
      timer = null;
      return;
    }
    schedule();
  };

  const schedule = () => {
    if (stopped) return;
    const elapsed = Date.now() - startedAt;
    if (elapsed >= maxDurationMs) {
      stopped = true;
      timer = null;
      return;
    }
    timer = setTimeout(() => void run(), elapsed >= slowAfterMs ? slowIntervalMs : intervalMs);
  };

  return {
    start: () => {
      if (!stopped) return;
      stopped = false;
      startedAt = Date.now();
      schedule();
    },
    stop: () => {
      stopped = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    },
    isRunning: () => !stopped,
  };
};
